import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  type KeyframeTracks,
  type Keyframe,
  type Interpolation,
  type EasingPreset,
  KeyframeEvaluator,
  ANIMATABLE_PROPERTIES,
  EASING_PRESETS,
} from '@/lib/keyframes'

const CURVE_STROKE = '#f59e0b'
const KEYFRAME_FILL = '#fbbf24'
const KEYFRAME_FILL_ACTIVE = '#f59e0b'
const KEYFRAME_STROKE = '#f59e0b'
const KEYFRAME_STROKE_ACTIVE = '#d97706'
const HANDLE_STROKE = '#d97706'
const PLAYHEAD_STROKE = '#3b82f6'
const GRID_STROKE = '#e5e5e5'

interface KeyframeCurveEditorProps {
  tracks: KeyframeTracks
  clipDuration: number
  currentTime: number
  onUpdateKeyframe: (property: string, index: number, updates: Partial<Keyframe>) => void
  onSelectProperty?: (property: string) => void
  selectedProperty?: string
}

const PADDING = { top: 20, right: 24, bottom: 28, left: 40 }
const DIAMOND_SIZE = 8
const HANDLE_SIZE = 5

export function KeyframeCurveEditor({
  tracks,
  clipDuration,
  currentTime,
  onUpdateKeyframe,
  onSelectProperty,
  selectedProperty: selectedPropertyProp,
}: KeyframeCurveEditorProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [dimensions, setDimensions] = useState({ width: 400, height: 200 })
  const [dragging, setDragging] = useState<{
    property: string
    index: number
    type: 'point' | 'handle'
    startX: number
    startY: number
    originalTime: number
    originalValue: number
  } | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; property: string; index: number
  } | null>(null)

  const activeProperties = useMemo(
    () => tracks.tracks.map(t => t.property).filter(p => p in ANIMATABLE_PROPERTIES),
    [tracks],
  )

  const selectedProperty = selectedPropertyProp ?? activeProperties[0]

  const track = useMemo(
    () => tracks.tracks.find(t => t.property === selectedProperty),
    [tracks, selectedProperty],
  )

  const propConfig = selectedProperty ? ANIMATABLE_PROPERTIES[selectedProperty] : null

  useEffect(() => {
    const el = svgRef.current?.parentElement
    if (!el) return
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      setDimensions({ width, height })
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const plotW = dimensions.width - PADDING.left - PADDING.right
  const plotH = dimensions.height - PADDING.top - PADDING.bottom
  const yMin = propConfig?.min ?? 0
  const yMax = propConfig?.max ?? 1

  const yRange = Math.max(yMax - yMin, 1e-6)
  const safePlotW = Math.max(plotW, 1)
  const safePlotH = Math.max(plotH, 1)

  const toSvg = useCallback((time: number, value: number) => ({
    x: PADDING.left + (time / Math.max(clipDuration, 0.01)) * safePlotW,
    y: PADDING.top + (1 - (value - yMin) / yRange) * safePlotH,
  }), [clipDuration, safePlotW, safePlotH, yMin, yRange])

  const fromSvg = useCallback((sx: number, sy: number) => ({
    time: Math.max(0, Math.min(clipDuration, ((sx - PADDING.left) / safePlotW) * clipDuration)),
    value: Math.max(yMin, Math.min(yMax, yMin + (1 - (sy - PADDING.top) / safePlotH) * yRange)),
  }), [clipDuration, safePlotW, safePlotH, yMin, yMax, yRange])

  const handlePointerDown = useCallback((
    e: React.PointerEvent, property: string, index: number, type: 'point' | 'handle' = 'point',
  ) => {
    e.stopPropagation()
    e.preventDefault()
    const kf = tracks.tracks.find(t => t.property === property)?.keyframes[index]
    if (!kf) return
    setDragging({
      property, index, type,
      startX: e.clientX, startY: e.clientY,
      originalTime: kf.time, originalValue: kf.value,
    })
    setContextMenu(null)
  }, [tracks])

  useEffect(() => {
    if (!dragging) return
    const onMove = (e: PointerEvent) => {
      const svg = svgRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      const { time, value } = fromSvg(sx, sy)

      if (dragging.type === 'point') {
        const roundedTime = Math.round(time * 100) / 100
        onUpdateKeyframe(dragging.property, dragging.index, { time: roundedTime, value })
      }
    }
    const onUp = () => setDragging(null)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [dragging, fromSvg, onUpdateKeyframe])

  const handleContextMenu = useCallback((e: React.MouseEvent, property: string, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ x: e.clientX, y: e.clientY, property, index })
  }, [])

  const setInterpolation = useCallback((interp: Interpolation) => {
    if (!contextMenu) return
    const kf = tracks.tracks.find(t => t.property === contextMenu.property)?.keyframes[contextMenu.index]
    if (!kf) return
    const easing = interp === 'Bezier'
      ? { preset: 'EaseInOut' as EasingPreset }
      : kf.easing
    onUpdateKeyframe(contextMenu.property, contextMenu.index, { interpolation: interp, easing })
    setContextMenu(null)
  }, [contextMenu, tracks, onUpdateKeyframe])

  const setEasingPreset = useCallback((preset: EasingPreset) => {
    if (!contextMenu) return
    onUpdateKeyframe(contextMenu.property, contextMenu.index, {
      interpolation: 'Bezier',
      easing: { preset },
    })
    setContextMenu(null)
  }, [contextMenu, onUpdateKeyframe])

  useEffect(() => {
    if (!contextMenu) return
    const close = () => setContextMenu(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [contextMenu])

  const curvePath = useMemo(() => {
    if (!track || track.keyframes.length < 2 || !propConfig) return ''
    const evaluator = new KeyframeEvaluator({ tracks: [track] })
    const pts: string[] = []
    const steps = Math.max(60, track.keyframes.length * 30)
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * clipDuration
      const val = evaluator.evaluate(track.property, t) ?? propConfig.default
      const { x, y } = toSvg(t, val)
      pts.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    }
    return pts.join(' ')
  }, [track, clipDuration, toSvg, propConfig])

  if (activeProperties.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Add keyframes to see the curve editor
      </div>
    )
  }

  const playheadX = toSvg(currentTime, 0).x

  const yTicks = 5
  const xTicks = Math.min(8, Math.max(3, Math.floor(clipDuration)))

  return (
    <div className="flex flex-col h-full relative">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-border shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
        {activeProperties.map(prop => (
          <button
            key={prop}
            onClick={() => onSelectProperty?.(prop)}
            className={cn(
              'px-2 py-0.5 text-[10px] font-medium rounded-sm whitespace-nowrap transition-colors',
              prop === selectedProperty
                ? 'bg-warning/15 text-warning'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            )}
          >
            {ANIMATABLE_PROPERTIES[prop]?.label ?? prop}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0">
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          className="w-full h-full"
          style={{ cursor: dragging ? 'grabbing' : 'default' }}
        >
          {/* Grid */}
          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const frac = i / yTicks
            const y = PADDING.top + frac * plotH
            const val = yMax - frac * (yMax - yMin)
            return (
              <g key={`yg-${i}`}>
                <line x1={PADDING.left} y1={y} x2={PADDING.left + plotW} y2={y}
                  stroke={GRID_STROKE} strokeWidth={0.5} />
                <text x={PADDING.left - 4} y={y + 3} textAnchor="end"
                  className="fill-muted-foreground" fontSize={9}>
                  {val % 1 === 0 ? val : val.toFixed(1)}
                </text>
              </g>
            )
          })}
          {Array.from({ length: xTicks + 1 }, (_, i) => {
            const frac = i / xTicks
            const x = PADDING.left + frac * plotW
            const t = frac * clipDuration
            return (
              <g key={`xg-${i}`}>
                <line x1={x} y1={PADDING.top} x2={x} y2={PADDING.top + plotH}
                  stroke={GRID_STROKE} strokeWidth={0.5} />
                <text x={x} y={PADDING.top + plotH + 14} textAnchor="middle"
                  className="fill-muted-foreground" fontSize={9}>
                  {t.toFixed(1)}s
                </text>
              </g>
            )
          })}

          {/* Curve */}
          {curvePath && (
            <path d={curvePath} fill="none" stroke={CURVE_STROKE} strokeWidth={1.5} strokeLinecap="round" />
          )}

          {/* Playhead */}
          <line x1={playheadX} y1={PADDING.top} x2={playheadX} y2={PADDING.top + plotH}
            stroke={PLAYHEAD_STROKE} strokeWidth={1} strokeDasharray="3,2" />

          {/* Keyframe diamonds */}
          {track?.keyframes.map((kf, i) => {
            const { x, y } = toSvg(kf.time, kf.value)
            const isAtTime = Math.abs(kf.time - currentTime) < 0.02
            return (
              <g key={`kf-${i}`}>
                <rect
                  x={x - DIAMOND_SIZE} y={y - DIAMOND_SIZE}
                  width={DIAMOND_SIZE * 2} height={DIAMOND_SIZE * 2}
                  transform={`rotate(45, ${x}, ${y})`}
                  fill={isAtTime ? KEYFRAME_FILL_ACTIVE : KEYFRAME_FILL}
                  stroke={isAtTime ? KEYFRAME_STROKE_ACTIVE : KEYFRAME_STROKE}
                  strokeWidth={1.5}
                  className="cursor-grab"
                  onPointerDown={(e) => handlePointerDown(e, selectedProperty!, i)}
                  onContextMenu={(e) => handleContextMenu(e, selectedProperty!, i)}
                />
                {kf.interpolation === 'Bezier' && i > 0 && (
                  <BezierHandles
                    kf={kf}
                    prevKf={track.keyframes[i - 1]}
                    toSvg={toSvg}
                  />
                )}
              </g>
            )
          })}
        </svg>
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-[140px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={e => e.stopPropagation()}
        >
          <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Interpolation</div>
          {(['Linear', 'Step', 'Bezier'] as Interpolation[]).map(interp => {
            const kf = tracks.tracks.find(t => t.property === contextMenu.property)?.keyframes[contextMenu.index]
            return (
              <button
                key={interp}
                onClick={() => setInterpolation(interp)}
                className={cn(
                  'w-full text-left px-3 py-1 text-xs hover:bg-muted transition-colors',
                  kf?.interpolation === interp ? 'text-warning font-medium' : 'text-muted-foreground',
                )}
              >
                {interp}
              </button>
            )
          })}
          <div className="h-px bg-border my-1" />
          <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Easing</div>
          {(['Linear', 'EaseIn', 'EaseOut', 'EaseInOut'] as EasingPreset[]).map(preset => {
            const kf = tracks.tracks.find(t => t.property === contextMenu.property)?.keyframes[contextMenu.index]
            return (
              <button
                key={preset}
                onClick={() => setEasingPreset(preset)}
                className={cn(
                  'w-full text-left px-3 py-1 text-xs hover:bg-muted transition-colors',
                  kf?.easing.preset === preset ? 'text-warning font-medium' : 'text-muted-foreground',
                )}
              >
                {preset === 'EaseInOut' ? 'Ease In-Out' : preset.replace(/([A-Z])/g, ' $1').trim()}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function BezierHandles({
  kf,
  prevKf,
  toSvg,
}: {
  kf: Keyframe
  prevKf: Keyframe
  toSvg: (time: number, value: number) => { x: number; y: number }
}) {
  const curve = kf.easing.preset === 'Custom' && kf.easing.custom_bezier
    ? kf.easing.custom_bezier
    : EASING_PRESETS[kf.easing.preset] ?? EASING_PRESETS.Linear

  const dt = kf.time - prevKf.time
  const dv = kf.value - prevKf.value

  const cp1Time = prevKf.time + curve.x1 * dt
  const cp1Val = prevKf.value + curve.y1 * dv
  const cp2Time = prevKf.time + curve.x2 * dt
  const cp2Val = prevKf.value + curve.y2 * dv

  const p1 = toSvg(prevKf.time, prevKf.value)
  const c1 = toSvg(cp1Time, cp1Val)
  const p2 = toSvg(kf.time, kf.value)
  const c2 = toSvg(cp2Time, cp2Val)

  return (
    <g className="pointer-events-none">
      <line x1={p1.x} y1={p1.y} x2={c1.x} y2={c1.y} stroke={HANDLE_STROKE} strokeWidth={0.75} strokeDasharray="2,1" />
      <line x1={p2.x} y1={p2.y} x2={c2.x} y2={c2.y} stroke={HANDLE_STROKE} strokeWidth={0.75} strokeDasharray="2,1" />
      <circle cx={c1.x} cy={c1.y} r={HANDLE_SIZE} fill="#fff" stroke={HANDLE_STROKE} strokeWidth={1} />
      <circle cx={c2.x} cy={c2.y} r={HANDLE_SIZE} fill="#fff" stroke={HANDLE_STROKE} strokeWidth={1} />
    </g>
  )
}
